import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Sparkles, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BrandingAnalysis {
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  source_website?: string;
  analysis: {
    logo_description?: string;
    primary_color_hex?: string;
    secondary_color_hex?: string;
  };
}

interface WebsiteAnalyzerProps {
  onApplyBranding: (branding: BrandingAnalysis) => Promise<void>;
}

export function WebsiteAnalyzer({ onApplyBranding }: WebsiteAnalyzerProps) {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<BrandingAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyzeWebsite = async () => {
    if (!websiteUrl) {
      toast.error('Please enter a website URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(websiteUrl);
    } catch {
      toast.error('Please enter a valid URL (including https://)');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysis(null);

    try {
      console.log('Calling analyze-website-branding function...');
      const { data, error: functionError } = await supabase.functions.invoke(
        'analyze-website-branding',
        {
          body: { url: websiteUrl }
        }
      );

      if (functionError) {
        console.error('Function error:', functionError);
        throw new Error(functionError.message || 'Failed to analyze website');
      }

      if (!data.success) {
        throw new Error(data.error || 'Analysis failed');
      }

      console.log('Analysis result:', data.branding);
      setAnalysis(data.branding);
      toast.success('Website analyzed successfully!');
    } catch (err) {
      console.error('Error analyzing website:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze website';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApply = async () => {
    if (!analysis) return;

    try {
      await onApplyBranding({
        ...analysis,
        source_website: websiteUrl
      });
      toast.success('Branding applied successfully!');
      setAnalysis(null);
      setWebsiteUrl('');
    } catch (err) {
      console.error('Error applying branding:', err);
      toast.error('Failed to apply branding');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          AI Website Analysis
        </CardTitle>
        <CardDescription>
          Automatically extract logo and colors from your company website
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription>
            Enter your company website URL and we'll use AI to extract your logo and brand colors.
            Estimated time: 10-15 seconds. Cost: ~$0.05 per analysis.
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <Input
            type="url"
            placeholder="https://yourcompany.com"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            disabled={isAnalyzing}
          />
          <Button onClick={analyzeWebsite} disabled={isAnalyzing}>
            {isAnalyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Analyze
              </>
            )}
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {analysis && (
          <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Analysis Complete
              </h4>
              <a 
                href={websiteUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                View Source
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <div className="space-y-3">
              {analysis.logo_url && (
                <div>
                  <p className="text-sm font-medium mb-2">Logo</p>
                  <div className="border rounded p-4 bg-background flex items-center justify-center">
                    <img 
                      src={analysis.logo_url} 
                      alt="Extracted logo" 
                      className="max-h-20 max-w-full object-contain"
                    />
                  </div>
                  {analysis.analysis.logo_description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {analysis.analysis.logo_description}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {analysis.primary_color && (
                  <div>
                    <p className="text-sm font-medium mb-2">Primary Color</p>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-12 h-12 rounded border"
                        style={{ backgroundColor: `hsl(${analysis.primary_color})` }}
                      />
                      <div className="text-xs">
                        <div className="font-mono">HSL: {analysis.primary_color}</div>
                        {analysis.analysis.primary_color_hex && (
                          <div className="font-mono text-muted-foreground">
                            {analysis.analysis.primary_color_hex}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {analysis.secondary_color && (
                  <div>
                    <p className="text-sm font-medium mb-2">Secondary Color</p>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-12 h-12 rounded border"
                        style={{ backgroundColor: `hsl(${analysis.secondary_color})` }}
                      />
                      <div className="text-xs">
                        <div className="font-mono">HSL: {analysis.secondary_color}</div>
                        {analysis.analysis.secondary_color_hex && (
                          <div className="font-mono text-muted-foreground">
                            {analysis.analysis.secondary_color_hex}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleApply} className="flex-1">
                Apply to Portal
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setAnalysis(null);
                  setWebsiteUrl('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
